import React, { useEffect, useState } from "react";
import {
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const SnCFunctionRoom = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [functionRoomCode, setFunctionRoomCode] = useState<string | null>(null);
  const [functionRoomName, setFunctionRoomName] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [codeFieldError, setCodeFieldError] = useState("");
  const [nameFieldError, setNameFieldError] = useState("");

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateInput = (): boolean => {
    setCodeFieldError("");
    setNameFieldError("");
    const codeLabel = "Function Room Code";
    const nameLabel = "Function Room Name";
    if (!functionRoomCode) {
      setCodeFieldError(codeLabel + validationMessages.sncMandatorySuffix);
    }
    if (!functionRoomName) {
      setNameFieldError(nameLabel + validationMessages.sncMandatorySuffix);
    }
    if (!functionRoomCode || !functionRoomName) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        functionRoomCode,
        validationLength.originCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(validationMessages.originCode);
      return false;
    }
    if (
      !validateString(
        functionRoomName,
        validationLength.originName,
        validationType.OTHER,
      )
    ) {
      setNameFieldError(validationMessages.originName);
      return false;
    }
    return true;
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (checkDuplicate(otherItems, "Function Room Code", functionRoomCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SnCPropertyEnum.functionRoom} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(items ?? [], "Function Room Code", functionRoomCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setCodeFieldError("");
      setNameFieldError("");
      setFunctionRoomCode(list["Function Room Code"] ?? null);
      setFunctionRoomName(list["Function Room Name"] ?? null);
    } else {
      setFunctionRoomCode(null);
      setFunctionRoomName(null);
      setCodeFieldError("");
      setNameFieldError("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  const payload = {
    "Function Room Code": functionRoomCode,
    "Function Room Name": functionRoomName,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Function Room
            <Tooltip
              title={tooltipComponent(
                golfTooltipHeaders?.categoryGroup ?? "Function Room",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack direction="column">
            <FormLabel component="legend">
              Function Room Code
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              placeholder="Function Room Code"
              value={functionRoomCode ?? ""}
              error={!!codeFieldError}
              helperText={codeFieldError}
              onChange={(e) => {
                setFunctionRoomCode(e.target.value || null);
                setCodeFieldError("");
              }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              Function Room Name
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              placeholder="Function Room Name"
              value={functionRoomName ?? ""}
              error={!!nameFieldError}
              helperText={nameFieldError}
              onChange={(e) => {
                setFunctionRoomName(e.target.value || null);
                setNameFieldError("");
              }}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Function Room
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Function Rooms Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code="Function Room Code"
              name="Function Room Name"
            />
          ) : (
            <Typography variant="body2">No Function Rooms Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCFunctionRoom;
